import React, { useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView, Animated, Platform } from 'react-native';
import DateTimePicker, { DateType, useDefaultStyles } from 'react-native-ui-datepicker';
import { COLORS } from '../../theme/themeTokens';
import { useSwipeDownDismiss } from '../../hooks/useSwipeDownDismiss';

export type PeriodResult = { days: number } | { from: Date; to: Date } | null;

interface Props {
  visible: boolean;
  onClose: (result: PeriodResult) => void;
}

// Отступ сверху под SimpleHeader (+ статус-бар на Android). iOS обычно
// SafeAreaView вокруг header'ов, тут его нет — поэтому padding-подобное
// значение подобрано так чтобы sheet визуально «упирался» верхом в
// нижнюю кромку хедера. Уменьшено с 90/100 → 70/85 после теста: при 90
// над sheet'ом ещё был виден кусок filter-row'а; теперь выравнивается
// точно с верхом content-area. Если понадобится точная привязка per-
// устройство — можно заменить на onLayout от header'а через prop.
const HEADER_OFFSET = Platform.OS === 'ios' ? 70 : 85;

const CardHistoryPeriodScreen: React.FC<Props> = ({ visible, onClose }) => {
  const [fromDate, setFromDate] = useState<DateType>(undefined);
  const [toDate, setToDate] = useState<DateType>(undefined);
  const [activePicker, setActivePicker] = useState<'from' | 'to' | null>(null);
  const defaultStyles = useDefaultStyles('light');
  // Swipe-down-to-close: тот же паттерн что у остальных bottom-sheet'ов
  // приложения (NumericKeypad, deposit picker'ы). translateY движется за
  // пальцем при drag'e вниз, past threshold → сама закрывает через
  // onClose(null).
  const { translateY, panHandlers, listProps } = useSwipeDownDismiss(visible, () => onClose(null));

  const resetState = () => {
    setFromDate(undefined);
    setToDate(undefined);
    setActivePicker(null);
  };

  const handleClose = (result: PeriodResult) => {
    resetState();
    onClose(result);
  };

  const handleApplyRange = () => {
    if (fromDate && toDate) {
      const from = new Date(fromDate as string | number);
      const to = new Date(toDate as string | number);
      handleClose({ from, to });
    }
  };

  const formatDateLabel = (d: DateType): string => {
    if (!d) return '';
    const date = new Date(d as string | number);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}`;
  };

  const calendarStyles = {
    ...defaultStyles,
    // Force light theme regardless of device settings
    calendarContainer: { backgroundColor: COLORS.pureLight },
    monthContainer: { backgroundColor: COLORS.pureLight },
    yearContainer: { backgroundColor: COLORS.pureLight },
    timeContainer: { backgroundColor: COLORS.pureLight },
    header: { color: COLORS.primaryNavy },
    month_label: { color: COLORS.primaryNavy },
    year_label: { color: COLORS.primaryNavy },
    day_label: { color: COLORS.primaryNavy },
    weekday_label: { color: COLORS.blueSteel },
    selected: { backgroundColor: COLORS.steelNavy },
    selected_label: { color: COLORS.white },
    today: { borderColor: COLORS.brandOrange, borderWidth: 1 },
    today_label: { color: COLORS.primaryNavy },
    outside_label: { color: '#C0C0C0' },
    button_prev: { tintColor: COLORS.primaryNavy },
    button_next: { tintColor: COLORS.primaryNavy },
    button_prev_image: { tintColor: COLORS.primaryNavy },
    button_next_image: { tintColor: COLORS.primaryNavy },
  };

  // Inline-паттерн (как NumericKeypad в OpenDepositScreen'e): sheet как
  // absolute-positioned View прямо в host'ском coordinate space, без
  // Modal и без fullscreen-backdrop'а. Host отвечает за "tap-outside-
  // closes" через свой Pressable wrap. `if (!visible) return null` —
  // компонент полностью исчезает при закрытии.
  if (!visible) return null;

  return (
    <Animated.View
      style={[styles.sheet, { transform: [{ translateY }] }]}
      {...panHandlers}
    >
      <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={styles.scrollContent} {...listProps}>
            <Text style={styles.title}>Period</Text>

            <TouchableOpacity style={styles.btn} onPress={() => handleClose({ days: 7 })} activeOpacity={0.7}>
              <Text style={styles.btnText}>Week</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.btn} onPress={() => handleClose({ days: 30 })} activeOpacity={0.7}>
              <Text style={styles.btnText}>Month</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.btn} onPress={() => handleClose({ days: 365 })} activeOpacity={0.7}>
              <Text style={styles.btnText}>Year</Text>
            </TouchableOpacity>

            {/* Date pickers: From / To */}
            <Text style={styles.dateLabel}>Choose period</Text>
            <View style={styles.dateRow}>
              <TouchableOpacity
                style={[styles.dateBtn, activePicker === 'from' && styles.dateBtnActive]}
                onPress={() => setActivePicker(activePicker === 'from' ? null : 'from')}
                activeOpacity={0.7}
              >
                <Text style={styles.dateBtnLabel}>From</Text>
                <Text style={styles.dateBtnValue}>{formatDateLabel(fromDate)}</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[styles.dateBtn, activePicker === 'to' && styles.dateBtnActive]}
                onPress={() => setActivePicker(activePicker === 'to' ? null : 'to')}
                activeOpacity={0.7}
              >
                <Text style={styles.dateBtnLabel}>To</Text>
                <Text style={styles.dateBtnValue}>{formatDateLabel(toDate)}</Text>
              </TouchableOpacity>
            </View>

            {/* Calendar for From */}
            {activePicker === 'from' && (
              <View style={styles.calendarWrap}>
                <DateTimePicker
                  mode="single"
                  date={fromDate}
                  onChange={({ date }) => {
                    setFromDate(date);
                    setActivePicker('to');
                  }}
                  maxDate={new Date().toISOString()}
                  styles={calendarStyles}
                />
              </View>
            )}

            {/* Calendar for To */}
            {activePicker === 'to' && (
              <View style={styles.calendarWrap}>
                <DateTimePicker
                  mode="single"
                  date={toDate}
                  onChange={({ date }) => {
                    setToDate(date);
                    setActivePicker(null);
                  }}
                  maxDate={new Date().toISOString()}
                  styles={calendarStyles}
                />
              </View>
            )}

            {/* Apply button */}
            {fromDate && toDate && !activePicker && (
              <TouchableOpacity
                style={styles.applyBtn}
                onPress={handleApplyRange}
                activeOpacity={0.7}
              >
                <Text style={styles.applyBtnText}>Apply</Text>
              </TouchableOpacity>
            )}
      </ScrollView>
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  // Inline sheet: absolute-positioned под SimpleHeader'ом. `top:
  // HEADER_OFFSET, bottom: 0` — sheet заполняет всё пространство ниже
  // header'а, verх упирается в нижнюю кромку хедера. Без Modal и без
  // fullscreen backdrop'а — tap-outside-closes через host'ский Pressable
  // wrap (см. CardHistoryScreen). Swipe-down закрывает через
  // useSwipeDownDismiss. zIndex/elevation держит sheet поверх scroll
  // content'а и TabBar'а. Mirror паттерна NumericKeypad inline mode.
  sheet: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: HEADER_OFFSET,
    bottom: 0,
    backgroundColor: COLORS.white,
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    padding: 16,
    zIndex: 10,
    elevation: 10,
  },
  scrollContent: {
    paddingBottom: 24,
  },
  title: {
    fontSize: 20,
    fontWeight: '600',
    color: COLORS.primaryNavy,
    marginBottom: 12,
    textAlign: 'left',
  },
  btn: {
    backgroundColor: COLORS.white,
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    marginBottom: 10,
    alignItems: 'flex-start',
  },
  btnActive: {
    backgroundColor: COLORS.steelNavy,
    borderColor: COLORS.steelNavy,
  },
  btnText: {
    fontFamily: 'Inter',
    lineHeight: 22,
    fontSize: 16,
    color: COLORS.primaryNavy,
    fontWeight: '500',
    textAlign: 'left',
  },
  btnTextActive: {
    color: COLORS.white,
  },
  dateLabel: {
    fontSize: 16,
    color: COLORS.primaryNavy,
    fontWeight: '600',
    marginTop: 14,
    marginBottom: 6,
    textAlign: 'left',
  },
  dateRow: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 4,
  },
  dateBtn: {
    flex: 1,
    backgroundColor: COLORS.white,
    borderRadius: 8,
    paddingVertical: 6,
    paddingHorizontal: 14,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  dateBtnActive: {
    borderColor: COLORS.steelNavy,
    borderWidth: 2,
  },
  dateBtnLabel: {
    fontSize: 17,
    color: COLORS.blueSteel,
    marginBottom: 1,
  },
  dateBtnValue: {
    fontSize: 16,
    fontWeight: '600',
    color: COLORS.primaryNavy,
  },
  calendarWrap: {
    marginTop: 4,
    borderRadius: 12,
    backgroundColor: COLORS.pureLight,
    padding: 8,
  },
  applyBtn: {
    marginTop: 12,
    backgroundColor: COLORS.steelNavy,
    borderRadius: 8,
    paddingVertical: 14,
    alignItems: 'center',
  },
  applyBtnDisabled: {
    opacity: 0.4,
  },
  applyBtnText: {
    fontFamily: 'Inter',
    lineHeight: 22,
    fontSize: 16,
    fontWeight: '500',
    color: COLORS.white,
  },
});

export default CardHistoryPeriodScreen;
